import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Trophy, ShieldCheck, ArrowUpRight, Crown, Medal, Award } from "lucide-react";
import { api } from "@/lib/api";

const tierStyles = {
  gold:   { bg: "bg-amber-50/70",  border: "border-amber-200",  base: "bg-gradient-to-b from-amber-400 to-amber-600",   iconBg: "bg-amber-100",  iconText: "text-amber-700",  badge: "text-amber-700  bg-amber-50  border-amber-200",  label: "Gold"   },
  silver: { bg: "bg-slate-100/70", border: "border-slate-300",  base: "bg-gradient-to-b from-slate-300 to-slate-500",  iconBg: "bg-slate-200", iconText: "text-slate-700", badge: "text-slate-700 bg-slate-50 border-slate-200", label: "Silver" },
  bronze: { bg: "bg-orange-50/70", border: "border-orange-200", base: "bg-gradient-to-b from-orange-400 to-orange-600", iconBg: "bg-orange-100", iconText: "text-orange-700", badge: "text-orange-700 bg-orange-50 border-orange-200", label: "Bronze" },
  watch:  { bg: "bg-rose-50/70",   border: "border-rose-200",   base: "bg-gradient-to-b from-rose-400 to-rose-600",     iconBg: "bg-rose-100",  iconText: "text-rose-700",   badge: "text-rose-700  bg-rose-50  border-rose-200",  label: "Watch"  },
};

function PodiumCard({ entry, rank, accent, height, Icon }) {
  return (
    <div className={`relative ${height} flex flex-col`} data-testid={`publisher-podium-${rank}`}>
      <div className={`flex-1 rounded-t-xl ${accent.bg} border ${accent.border} flex flex-col items-center justify-center px-4 pt-6 pb-4 shadow-soft relative overflow-hidden`}>
        <div className={`absolute -top-10 left-1/2 -translate-x-1/2 size-32 rounded-full ${accent.iconBg} blur-2xl opacity-60`} />
        <div className={`relative flex items-center justify-center size-12 rounded-full ${accent.iconBg} ${accent.iconText} mb-3`}>
          <Icon className="size-5" strokeWidth={2.2} />
        </div>
        <div className="font-semibold text-slate-900 tracking-tight relative line-clamp-1 max-w-[14ch]">{entry.name}</div>
        <div className="mt-3 text-3xl font-semibold text-slate-900 tabular-nums relative">{entry.score?.toFixed(1)}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 relative">quality score · 30d</div>
        <div className="mt-2 text-[11px] text-slate-600 tabular-nums">{entry.accepted.toLocaleString()} accepted</div>
      </div>
      <div className={`h-10 rounded-b-xl ${accent.base} flex items-center justify-center text-white font-mono font-semibold text-sm tracking-widest`}>#{rank}</div>
    </div>
  );
}

export default function PublishersLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/leaderboard")
      .then((r) => setData(r.data))
      .catch(() => setData({ top: [] }))
      .finally(() => setLoading(false));
  }, []);

  const top = data?.top || [];
  const podium = [top[1], top[0], top[2]].filter(Boolean);
  const rest = top.slice(3);
  const podiumIcons = [Medal, Crown, Award];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="publishers-leaderboard">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_-10%,rgba(0,85,255,0.08),transparent_60%)] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24 relative">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#0055FF] font-semibold mb-4">
            <Trophy className="size-3.5" /> Publisher Quality Leaderboard
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold text-slate-900 max-w-3xl leading-[1.05]">
            The lead producers buyers<br />
            <span className="bg-gradient-to-r from-[#0055FF] to-[#22D3A6] bg-clip-text text-transparent">trust the most</span>.
          </h1>
          <p className="mt-5 text-slate-600 max-w-2xl text-[15px] leading-relaxed">
            Top 10 publishers ranked by 30-day quality score — accept rate weighted 60%, kept-rate (not returned) 40%.
            Only opted-in producers are listed. Scoring is opaque to publishers and never adjusted manually.
          </p>
          <div className="mt-7 inline-flex items-center gap-2 text-[12px] text-slate-500">
            <ShieldCheck className="size-3.5 text-emerald-600" />
            Updated hourly · Minimum 20 leads in window
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        {loading && <div className="text-center text-sm text-slate-500">Loading…</div>}

        {!loading && top.length === 0 && (
          <div className="text-center py-16 text-sm text-slate-500" data-testid="leaderboard-empty">
            No publishers ranked yet. Be the first to opt in from your publisher dashboard → Settings.
          </div>
        )}

        {top.length > 0 && (
          <>
            {/* Podium */}
            <div className="grid grid-cols-3 gap-3 sm:gap-5 items-end mb-12">
              {podium.map((entry, idx) => {
                const rank = idx === 0 ? 2 : idx === 1 ? 1 : 3;
                const accent = tierStyles[entry.tier] || tierStyles.bronze;
                const Icon = podiumIcons[rank - 1] || Award;
                const height = rank === 1 ? "h-64 sm:h-72" : rank === 2 ? "h-56 sm:h-64" : "h-52 sm:h-60";
                return <PodiumCard key={rank} entry={entry} rank={rank} accent={accent} height={height} Icon={Icon} />;
              })}
            </div>

            {/* Rest of table */}
            {rest.length > 0 && (
              <Card className="rounded-xl shadow-soft border-slate-200 overflow-hidden">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/60 border-b border-slate-200">
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500 text-left">
                        <th className="py-3 px-5 font-medium w-12">Rank</th>
                        <th className="font-medium">Publisher</th>
                        <th className="font-medium">Tier</th>
                        <th className="font-medium text-right">Accepted</th>
                        <th className="font-medium text-right">Kept rate</th>
                        <th className="font-medium text-right pr-5">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rest.map((entry, i) => {
                        const accent = tierStyles[entry.tier] || tierStyles.bronze;
                        const rank = i + 4;
                        return (
                          <tr key={rank} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`publisher-row-${rank}`}>
                            <td className="py-3.5 px-5 font-mono text-slate-500 tabular-nums">#{rank}</td>
                            <td className="py-3.5 font-medium text-slate-900">{entry.name}</td>
                            <td className="py-3.5">
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${accent.badge}`}>{accent.label}</span>
                            </td>
                            <td className="py-3.5 text-right tabular-nums text-slate-700">{entry.accepted.toLocaleString()}</td>
                            <td className="py-3.5 text-right tabular-nums text-slate-700">{(entry.kept_rate * 100).toFixed(1)}%</td>
                            <td className="py-3.5 pr-5 text-right font-mono font-semibold text-slate-900 tabular-nums">{entry.score?.toFixed(1)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-slate-50/60">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 max-w-2xl mx-auto">
            Send us quality, climb the board, attract premium buyers.
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl mx-auto text-[15px]">Become a Connexis publisher — earn 90% of every accepted lead and let your quality speak for itself.</p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/publisher/apply">
              <Button className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="publisher-leaderboard-cta-apply">
                Apply to sell us your leads <ArrowUpRight className="size-4 ml-1" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="rounded-full" data-testid="publisher-leaderboard-cta-pricing">View buyer pricing</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
